import { Alert, Box, Button, Card, Heading, HStack, Spacer, Text, useDisclosure, VStack } from '@chakra-ui/react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BaseButton, BaseButtonText, BaseDialog, BaseIcon } from '@cks-internal/react-base';
import { faClock, faInfoCircle, faUser } from '@fortawesome/free-solid-svg-icons';
import { EntityNoteQueryType } from '../../enums/common/EntityNoteQueryType';
import { EntityNoteType } from '../../enums/common/EntityNoteType';
import { useState } from 'react';
import { EntityNotesFilterModel } from './types/entityNotesFilterModel';
import { EntityNoteTypeBadge } from './types/entityNoteTypeBadge';
import { useMultipleEntitiesNotesQuery } from './apis/useMultipleEntitiesNotesQuery';
import { getFormattedServerDate } from '../../util/helpers/dateHelpers';
import EntityNoteTravellerDisplay from './EntityNoteTravellerDisplay';

type EntityNoteWarningProps = {
  entityIds: number[];
  queryType: EntityNoteQueryType;
  noteType: EntityNoteType;
};
/**
 * EntityNotesWarning component checks for entity-specific notes of a given type
 * and displays an error alert with details if any are found.
 *
 * It:
 * - Queries notes for each entity ID using `useMultipleEntitiesNotesQuery`.
 * - Determines loading, error, and data presence states.
 * - Renders an `Alert` with the first matching note when present.
 * - Displays a "Details" button that opens a dialog showing all notes of that type,
 *   including author and timestamp.
 *
 * @component
 * @param {{ entityIds: number[]; queryType: EntityNoteQueryType; noteType: EntityNoteType }} props
 * @param {number[]} props.entityIds - Array of entity IDs to fetch notes for.
 * @param {EntityNoteQueryType} props.queryType - The scope/type of entity note query (e.g., Company, Contact).
 * @param {EntityNoteType} props.noteType - The specific note type to filter (e.g., CodeRed).
 * @returns {JSX.Element|null} An alert and dialog UI if notes exist, otherwise null.
 */
export const EntityNotesWarning = (props: EntityNoteWarningProps) => {
  const disclosure = useDisclosure();
  const [filterState, _setFilterState] = useState<EntityNotesFilterModel>({
    Search: '',
    Active: true,
    EntityNoteTypes: [props.noteType],
    EntityNoteQueryType: props.queryType
  });
  const notesQueries = useMultipleEntitiesNotesQuery(props.entityIds, filterState);

  // Check if any of the queries are loading or have errors
  const isLoading = notesQueries.some((query) => query.isLoading);
  const hasError = notesQueries.some((query) => query.isError);

  // Check if any of the queries have data
  const hasData = notesQueries.some((query) => query.data && query.data.length > 0);
  // Check if any of the queries have notes
  const hasNotes = notesQueries.some((query) => query.data && query.data.some((entityNote) => entityNote.Note));
  // Check if any of the queries have notes
  const hasNotesWithType = notesQueries.some(
    (query) => query.data && query.data.some((entityNote) => entityNote.EntityNoteType === props.noteType)
  );

  if (!isLoading && !hasError && hasData && hasNotes && hasNotesWithType) {
    return (
      <>
        <Alert.Root
          status='error'
          marginBottom={'8px'}
          marginTop={'24px'}
          padding={'16px'}
          borderRadius={'8px'}
          alignItems={'center'}>
          <Alert.Indicator>
            <FontAwesomeIcon icon={faInfoCircle} />
          </Alert.Indicator>
          <Alert.Title paddingLeft={'8px'} width={'80%'}>
            <Heading size={'sm'} lineClamp={2}>
              {
                notesQueries
                  .find((query) => query.data?.some((entityNote) => entityNote.Note))
                  ?.data?.find((entityNote) => entityNote.Note)?.Note
              }
            </Heading>
          </Alert.Title>
          <Spacer />
          <Button size='sm' minW='0' w='auto' padding={'0px 16px 0px 16px'} onClick={disclosure.onOpen}>
            Details
          </Button>
        </Alert.Root>
        {/* Overlay edit booking history filters component */}

        <BaseDialog
          disableCloseOnClick={false}
          closeOnEsc={true}
          disclosure={disclosure}
          isLoading={false}
          header={
            <HStack gap={'0px'}>
              <EntityNoteTypeBadge noteType={props.noteType} />
            </HStack>
          }
          footerRender={
            <>
              <BaseButton onClick={disclosure.onClose} buttonText={BaseButtonText.OK} />
            </>
          }
          size={{ base: 'sm' }}>
          <Box marginTop={'24px'} marginBottom={'24px'}>
            {notesQueries.map((queries, index) => {
              const queryHasNotesOfType =
                queries.data && queries.data.some((entityNote) => entityNote.EntityNoteType === props.noteType);
              return (
                queryHasNotesOfType && (
                  <Card.Root
                    key={index}
                    maxWidth={'1150px'}
                    margin={'auto'}
                    marginBottom={'16px'}
                    padding={'16px'}
                    border={'1px solid rgb(221, 222, 224)'}
                    boxShadow={'none'}
                    borderRadius={'8px'}>
                    {props.queryType === EntityNoteQueryType.Contact && queries.data && (
                      <EntityNoteTravellerDisplay travellerId={props.entityIds[index]} />
                    )}
                    {queries.data?.map((value, index2) => {
                      return (
                        <Card.Body padding={'px 0px 25px 0px'} key={index2}>
                          <VStack gap={'8px'} padding={0} alignItems={'flex-start'}>
                            <Text fontSize={'sm'}>{value.Note}</Text>
                            <HStack width={'100%'} paddingLeft={'4px'} paddingRight={'8px'}>
                              <HStack gap={'4px'}>
                                <BaseIcon color={'primary.500'} height={'12px'} icon={faUser} />
                                <Text fontSize={'xs'} fontWeight={'semibold'}>
                                  {value.CreatedByName}
                                </Text>
                              </HStack>
                              <Spacer />
                              {value.CreatedOn && (
                                <HStack gap={'4px'}>
                                  <BaseIcon color={'primary.500'} height={'12px'} icon={faClock} />
                                  <Text fontSize={'xs'} fontWeight={'semibold'}>
                                    {getFormattedServerDate(value.CreatedOn)}
                                  </Text>
                                </HStack>
                              )}
                            </HStack>
                          </VStack>
                        </Card.Body>
                      );
                    })}
                  </Card.Root>
                )
              );
            })}
          </Box>
        </BaseDialog>
      </>
    );
  }
};
